import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Col, Container, Row } from 'react-bootstrap'

import { useCurrentUserIntegrations } from '../../Integration/useCurrentUserIntegrations'

import { IntegrationEmail, IntegrationEmailThread } from '../../../models/Email'

import { Button } from '../../../components/Button/Button'

import { selectShouldPollEmailThreadBusinessEvents, selectShouldPollEmailThreadTasks } from '../../../store/emailHub/emailHub'
import { selectShouldReload, setShouldReload, setUpdateStartedFrom } from '../../../store/meetingSummary/contactUpdate'
import { startBusinessEventPolling, stopBusinessEventPolling } from '../../../store/businessEvent/businessEvent'
import { selectEmailThreads, selectEmailThreadsLoading } from '../../../store/emailHub/emailThreads'
import { selectContactsForThread } from '../../../store/emailHub/emailUsers'
import { selectTasks, startTaskPolling, stopTaskPolling } from '../../../store/task/task'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'

import { AddTaskToCRMModal } from '../../MeetingSummary/CRM/V2/AddTaskToCRMModal'
import { TaskSourceType } from '../../Tasks/hooks'

import { UpdateExternalEmailContactModal } from '../../UpdateExternalContact/UpdateExternalContact'
import { EmailResponseView } from '../EmailResponse/EmailResponseView'
import { useLastContactEmailFromThread } from '../Table/hooks'
import { EmailSidebar } from '../EmailSidebar/EmailSidebar'
import { useFetchEmailThreadTasks } from '../hooks'

import { EmailThreadAttachments } from '../EmailResponse/EmailThreadAttachments'
import { EmailThreadContacts } from '../EmailResponse/EmailThreadContacts'
import { EmailResponseChat } from '../EmailResponse/EmailResponseChat'
import { EmailThreadTasks } from '../EmailResponse/EmailThreadTasks'
import { EmailPluginPromo } from './EmailPluginPromo'
import { EmailView } from './EmailView'

import './EmailThreadView.scss'

export interface EmailThreadViewProps {
  emailConnector: string
  threadId: string
}

export const EmailThreadView: React.FC<EmailThreadViewProps> = ({ emailConnector, threadId }) => {
  const dispatch = useAppDispatch()

  const emailThreads = useAppSelector(selectEmailThreads)
  const emailThreadsLoading = useAppSelector(selectEmailThreadsLoading)

  const shouldPollTasks = useAppSelector((state) => selectShouldPollEmailThreadTasks(state, threadId))
  const shouldPollEmailBusinessEvents = useAppSelector((state) => selectShouldPollEmailThreadBusinessEvents(state, threadId))

  useEffect(() => {
    if (shouldPollTasks) {
      dispatch(startTaskPolling())
    } else {
      dispatch(stopTaskPolling())
    }
  }, [dispatch, shouldPollTasks])

  useEffect(() => {
    if (shouldPollEmailBusinessEvents) {
      dispatch(startBusinessEventPolling())
    } else {
      dispatch(stopBusinessEventPolling())
    }
  }, [dispatch, shouldPollEmailBusinessEvents])

  if (emailThreadsLoading) {
    return (
      <div className='p-4'>
        <div className='skeleton' style={{ width: '100%', height: '300px' }} />
      </div>
    )
  }

  const emailThread = emailThreads.find((thread) => thread.externalId === threadId)

  if (!emailThread) {
    return <div className='p-4'>No email thread found</div>
  }

  return (
    <>
      <EmailThreadBody emailConnector={emailConnector} thread={emailThread} />
      <EmailSidebar />
    </>
  )
}

interface EmailThreadBodyProps {
  emailConnector: string
  thread: IntegrationEmailThread
}

const EmailThreadBody: React.FC<EmailThreadBodyProps> = ({ emailConnector, thread }) => {
  const { externalId: threadId } = thread
  const dispatch = useAppDispatch()
  const { taskExportSupportedIntegrations, getContactCreateOrUpdateSupportedIntegrations } = useCurrentUserIntegrations()
  const contactsForThread = useAppSelector(selectContactsForThread(threadId))
  const tasks = useAppSelector(selectTasks)
  const shouldReload = useAppSelector(selectShouldReload)

  const [showAllEmails, setShowAllEmails] = useState<boolean>(false)

  const navigate = useNavigate()
  const { modal = null, serviceId: modalServiceId = null, contactId: modalContactId = null } = useParams()

  const { lastEmailByContact } = useLastContactEmailFromThread(thread)
  const { fetchTasks } = useFetchEmailThreadTasks()

  const emailsToDisplay = useMemo<IntegrationEmail[]>(() => {
    const { emails } = thread

    if (showAllEmails) {
      return emails
    }

    const firstEmail = emails[0]
    const lastEmail = emails[emails.length - 1]

    if (emails.length === 1) {
      return [firstEmail]
    }

    if (lastEmailByContact && lastEmailByContact.externalId !== firstEmail.externalId) {
      return [firstEmail, lastEmailByContact]
    }

    return [firstEmail, lastEmail]
  }, [lastEmailByContact, showAllEmails, thread])

  const selectedTaskExportIntegration = taskExportSupportedIntegrations.find(({ serviceId }) => serviceId === modalServiceId) ?? null

  const selectedContact = useMemo(() => contactsForThread.find((e) => e.id === modalContactId) ?? null, [contactsForThread, modalContactId])

  const selectedContactUpdateIntegration =
    (selectedContact && getContactCreateOrUpdateSupportedIntegrations(selectedContact).find(({ serviceId }) => serviceId === modalServiceId)) ?? null

  const showUpdateParticipantModal = modal === 'contact-update' && !!selectedContact && !!selectedContactUpdateIntegration

  const handleHideModal = async (isSaved?: boolean) => {
    navigate('.')
    if (isSaved) {
      await fetchTasks(emailConnector, threadId)
    }
    dispatch(setUpdateStartedFrom(null))
  }

  useEffect(() => {
    if (!showUpdateParticipantModal && shouldReload) {
      dispatch(setShouldReload(false))
      navigate(0)
    }
  }, [showUpdateParticipantModal, shouldReload, dispatch, navigate])

  useEffect(() => {
    if (showUpdateParticipantModal) {
      dispatch(setUpdateStartedFrom('email'))
    }
  }, [showUpdateParticipantModal, dispatch])

  return (
    <>
      {modal === 'contact-update' && selectedContact && selectedContactUpdateIntegration ? (
        <UpdateExternalEmailContactModal
          user={selectedContact}
          serviceId={selectedContactUpdateIntegration.serviceId}
          emailConnector={emailConnector}
          threadId={threadId}
          show={true}
          onClose={handleHideModal}
        />
      ) : null}
      {modal === 'export-tasks' && selectedTaskExportIntegration && lastEmailByContact ? (
        <AddTaskToCRMModal
          show={true}
          onHide={handleHideModal}
          userIntegration={selectedTaskExportIntegration}
          tasks={tasks}
          taskContextDetails={{ threadId, emailConnector, emailId: lastEmailByContact.externalId }}
          type={TaskSourceType.EMAIL}
        />
      ) : null}
      {/*{modal === 'client-profile' && selectedContact ? (*/}
      {/*  <EmailResponseClientProfileModal show={true} onHide={handleHideModal} emailConnector={emailConnector} threadId={threadId} contact={selectedContact} />*/}
      {/*) : null}*/}
      <Container className='m-0 p-0' fluid>
        <Row className='m-0 vh-100'>
          <Col className='px-3 zocks-pb-5.0 h-100 overflow-y-auto email-thread-view-left-container'>
            <div className='d-flex flex-column'>
              {emailsToDisplay.map((email, index) => (
                <div className='position-relative'>
                  {thread.emails.length > 2 && index === emailsToDisplay.length - 1 ? (
                    <EmailThreadShowMoreButton showAllEmails={showAllEmails} onClick={() => setShowAllEmails((prev) => !prev)} />
                  ) : null}
                  <EmailView key={email.externalId} email={email} initialCollapsed={index !== emailsToDisplay.length - 1} />
                </div>
              ))}
              <EmailResponseView thread={thread} />
            </div>
          </Col>
          <Col
            xs='auto'
            className='p-3 zocks-pb-5.0 h-100 overflow-y-auto zocks-bg-color-gray-100 zocks-border-color-gray-300 border-start email-thread-view-right-container'
          >
            <div className='d-flex flex-column gap-3'>
              <EmailPluginPromo />
              <EmailResponseChat threadId={threadId} />
              <EmailThreadContacts threadId={threadId} />
              <EmailThreadAttachments emailConnector={emailConnector} threadId={threadId} />
              {lastEmailByContact ? <EmailThreadTasks emailConnector={emailConnector} threadId={threadId} emailId={lastEmailByContact.externalId} /> : null}
            </div>
          </Col>
        </Row>
      </Container>
    </>
  )
}

interface EmailThreadShowMoreButton {
  showAllEmails: boolean
  onClick: () => void
}

const EmailThreadShowMoreButton: React.FC<EmailThreadShowMoreButton> = ({ showAllEmails, onClick }) => {
  return (
    <div className='w-100 position-absolute d-flex align-items-center justify-content-center border-bottom zocks-border-color-gray-200 zocks-bg-color-white email-thread-view-load-button-container z-1'>
      <Button variant='secondaryGhostButton' className='py-2 zocks-px-0.75 button-medium' onClick={onClick}>
        {showAllEmails ? (
          <>
            <span>Hide older messages</span>
            <i className='bi bi-chevron-contract' />
          </>
        ) : (
          <>
            <span>Show more</span>
            <i className='bi bi-chevron-expand' />
          </>
        )}
      </Button>
    </div>
  )
}
